import {
  Children,
  forwardRef,
  type ButtonHTMLAttributes,
  type ReactNode,
} from "react";
import { LoadingSpinner } from "./LoadingSpinner";

// Canonical surface (4 variants × 3 sizes, 8px radius). All redesigned
// surfaces should use these. The legacy values below are accepted for
// backward-compatibility with non-redesigned callers and resolve to one
// of the canonical variants/sizes.
type ButtonVariant =
  | "link"
  | "primary"
  | "secondary"
  | "outline"
  // legacy aliases
  | "ghost"
  | "accent"
  | "danger"
  | "danger-outline";

type ButtonSize =
  | "sm"
  | "md"
  | "lg"
  // legacy aliases
  | "xs"
  | "xl"
  | "touch"
  | "auto";

// Legacy prop kept so existing callers compile. Always 8px radius now —
// the value is ignored.
type ButtonShape = "rounded" | "soft" | "pill" | "round" | "square";

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: ButtonShape;
  fullWidth?: boolean;
  isLoading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
};

export type ButtonClassOptions = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  shape?: ButtonShape;
  fullWidth?: boolean;
  iconOnly?: boolean;
  className?: string;
};

const variantClasses: Record<ButtonVariant, string> = {
  link: "bg-transparent text-[color:var(--text-primary)] hover:bg-[color:var(--surface-alt)]",
  ghost:
    "bg-transparent text-[color:var(--text-primary)] hover:bg-[color:var(--surface-alt)]",
  primary:
    "bg-[color:var(--primary-500)] text-[color:var(--on-accent,#fff)] border-[length:var(--border-control-width,1px)] border-[color:var(--primary-500)] hover:bg-[color:var(--primary-600)] hover:border-[color:var(--primary-600)]",
  secondary:
    "bg-[color:var(--accent-500)] text-[color:var(--on-accent,var(--primary-500))] border-[length:var(--border-control-width,1px)] border-[color:var(--accent-500)] hover:bg-[color:var(--accent-600)] hover:border-[color:var(--accent-600)]",
  accent:
    "bg-[color:var(--accent-500)] text-[color:var(--on-accent,var(--primary-500))] border-[length:var(--border-control-width,1px)] border-[color:var(--accent-500)] hover:bg-[color:var(--accent-600)] hover:border-[color:var(--accent-600)]",
  outline:
    "bg-[color:var(--surface)] text-[color:var(--text-primary)] border-[length:var(--border-control-width,1px)] border-[color:var(--border-strong)] hover:bg-[color:var(--surface-alt)]",
  danger:
    "bg-[color:var(--danger-500,var(--error-500))] text-white border-[length:var(--border-control-width,1px)] border-[color:var(--danger-500,var(--error-500))] hover:bg-[color:var(--danger-600,var(--error-600))] hover:border-[color:var(--danger-600,var(--error-600))]",
  // Destructive SECONDARY action. A solid red fill is reserved for the one
  // primary destructive CTA on a screen; a row that offers Void and Comp on
  // every line would otherwise be a wall of red, which reads as an error state
  // rather than as available actions.
  "danger-outline":
    "bg-[color:var(--surface)] text-[color:var(--danger-500,var(--error-500))] border-[length:var(--border-control-width,1px)] border-[color:var(--danger-500,var(--error-500))] hover:bg-[color:var(--error-50)]",
};

const sizeClasses: Record<ButtonSize, string> = {
  xs: "h-7 px-2.5 text-[11.5px] gap-1",
  sm: "h-7 px-2.5 text-[11.5px] gap-1",
  md: "h-[34px] px-3.5 text-[13px] gap-1.5",
  lg: "h-10 px-[18px] text-sm gap-2",
  xl: "h-10 px-[18px] text-sm gap-2",
  // Touch/POS scale. 44px is the tap-target floor for a device used one-handed
  // on a busy floor; the desktop-density sizes above are all below it.
  touch: "h-11 px-4 text-sm gap-2",
  auto: "gap-2",
};

const iconOnlySizeClasses: Record<ButtonSize, string> = {
  xs: "h-7 w-7 p-0 text-[11.5px]",
  sm: "h-7 w-7 p-0 text-[11.5px]",
  md: "h-[34px] w-[34px] p-0 text-[13px]",
  lg: "h-10 w-10 p-0 text-sm",
  xl: "h-10 w-10 p-0 text-sm",
  touch: "h-11 w-11 p-0 text-sm",
  auto: "p-0",
};

function cx(...classes: Array<string | false | null | undefined>) {
  return classes.filter(Boolean).join(" ");
}

export function buttonClass({
  variant = "link",
  size = "md",
  fullWidth = false,
  iconOnly = false,
  className,
}: ButtonClassOptions = {}) {
  return cx(
    "inline-flex items-center justify-center font-semibold rounded-[var(--radius-control,8px)] font-[family-name:var(--font-action,var(--font-body))] shadow-[var(--shadow-control,0_0_0_0_transparent)] transition-colors select-none",
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--accent-500)]",
    "disabled:opacity-[0.55] disabled:cursor-not-allowed",
    variantClasses[variant],
    iconOnly ? iconOnlySizeClasses[size] : sizeClasses[size],
    fullWidth && "w-full",
    className,
  );
}

/**
 * Everything a NON-button element needs to look like a `Button`: the classes
 * AND the hooks the look stylesheets select on. Use it on a `<Link>` that is a
 * button visually but must stay an anchor (navigation a user can open in a new
 * tab):
 *
 *   <Link to="…" {...buttonProps({ variant: "outline", size: "sm" })}>
 *
 * `buttonClass()` alone is not enough, and that is why this exists: the looks
 * style `[data-ui="button"][data-variant=…][data-size=…]`, so an anchor wearing
 * only the classes kept the platform defaults (a 1px border, no hard shadow)
 * while every real Button beside it was redrawn by the look.
 */
export function buttonProps(options: ButtonClassOptions = {}) {
  const { variant = "link", size = "md", iconOnly = false } = options;
  return {
    className: buttonClass(options),
    "data-ui": "button",
    "data-variant": variant,
    "data-size": size,
    ...(iconOnly ? { "data-icon-only": true } : {}),
  } as const;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      type = "button",
      variant = "link",
      size = "md",
      /**
       * ACCEPTED AND IGNORED. Radius is a fixed 8px for every button on the
       * platform. Kept only so the legacy call sites that still pass
       * `shape="pill"` — including the seller's own `PageActionButton` — keep
       * type-checking; it has never had an effect. Do not add new callers: a
       * prop that reads as a request and silently isn't one is how a "why
       * doesn't this look like the seller" bug gets written on purpose.
       */
      shape: _shape,
      fullWidth = false,
      isLoading = false,
      disabled,
      leadingIcon,
      trailingIcon,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const isDisabled = Boolean(disabled || isLoading);
    const hasChildren = Children.count(children) > 0;
    const iconOnly = !hasChildren && Boolean(leadingIcon || trailingIcon);

    return (
      <button
        ref={ref}
        type={type}
        disabled={isDisabled}
        aria-busy={isLoading || undefined}
        data-ui="button"
        data-variant={variant}
        data-size={size}
        /* Marks an icon-only button for the look stylesheets. Without it a
           look's size rule (`[data-size="sm"] { padding: 0 12px }`) pads an
           icon button out of square and can crop its glyph to nothing; with
           it, a look can say "icon buttons are square" once. */
        data-icon-only={iconOnly || undefined}
        className={buttonClass({
          variant,
          size,
          fullWidth,
          iconOnly,
          className,
        })}
        {...props}
      >
        {isLoading ? (
          <span aria-hidden="true" className="inline-flex shrink-0">
            <LoadingSpinner />
          </span>
        ) : leadingIcon ? (
          <span aria-hidden="true" className="inline-flex shrink-0">
            {leadingIcon}
          </span>
        ) : null}
        {hasChildren ? (
          <span className="whitespace-nowrap">{children}</span>
        ) : null}
        {trailingIcon ? (
          <span aria-hidden="true" className="inline-flex shrink-0">
            {trailingIcon}
          </span>
        ) : null}
      </button>
    );
  },
);

Button.displayName = "Button";
